import React from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * OFCard — Card de Ordem de Fabrico (Layout Mockup Samidel)
 * OF numero (grande) + badge URGENTE/PROTO | Modelo | Marca | Qt + Lotes + Ops + seta
 */
export default function OFCard({
  of,
  onClick,
  operacoesCongeladas = [],
}) {
  const isUrgente = of.urgente;
  const lotes = (of.lotes || []).filter(l => l.status === 'aprovado' || !l.status);
  const allOps = lotes.flatMap(l => l.operacoes_operador || []);
  const uniqueOps = new Set(allOps.map(op => op.operacao_id)).size;
  const qtdTotal = lotes.reduce((sum, l) => sum + (l.quantidade_ajustada || l.quantidade || 0), 0);

  // Calcular operações concluídas vs total
  const opsConcluidasCount = allOps.filter(op => {
    const max = op.quantidade_total || 1;
    const produzido = op.quantidade_produzida_todos ?? op.quantidade_produzida ?? 0;
    return produzido >= max;
  }).length;
  const opsTotal = allOps.length;
  const todasConcluidas = opsTotal > 0 && opsConcluidasCount >= opsTotal;

  // Verificar se tem categoria especial (PROTO, Amostra, etc.)
  const hasEspecial = lotes.some(l => {
    const cat = (l.categoria_nome || l.categoria || '').toLowerCase();
    return cat === 'proto' || cat === 'especial' || cat === 'amostra';
  });
  const categoriaNome = lotes.find(l => l.categoria_nome)?.categoria_nome;

  const borderColor = isUrgente ? '#EF4444' : hasEspecial ? '#7C3AED' : '#E2E8F0';
  const borderWidth = isUrgente || hasEspecial ? '2.5px' : '1.5px';

  return (
    <div
      className="rounded-2xl overflow-hidden cursor-pointer transition-transform active:scale-[0.98] hover:shadow-lg"
      onClick={onClick}
      data-testid={`of-card-${of.numero_of}`}
      style={{
        background: hasEspecial ? '#FAF5FF' : '#FFFFFF',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: `${borderWidth} solid ${borderColor}`,
      }}
    >
      <div className="px-4 py-4 flex items-center gap-3">
        {/* Left: OF info */}
        <div className="flex-1 min-w-0">
          {/* Line 1: OF number (grande) + badges */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-black text-xl leading-tight" style={{ color: '#1E293B' }} data-testid={`of-number-${of.numero_of}`}>
              OF {of.numero_of}
            </span>
            {isUrgente && (
              <span
                className="flex items-center px-2.5 py-1 rounded-full text-[10px] font-extrabold text-white uppercase tracking-wide"
                style={{ background: '#EF4444' }}
                data-testid={`of-urgente-${of.numero_of}`}
              >
                URGENTE
              </span>
            )}
            {hasEspecial && categoriaNome && (
              <span
                className="flex items-center px-2.5 py-1 rounded-full text-[10px] font-extrabold text-white uppercase tracking-wide"
                style={{ background: '#7C3AED' }}
                data-testid={`of-especial-${of.numero_of}`}
              >
                {categoriaNome}
              </span>
            )}
          </div>
          {/* Line 2: Modelo (artigo) */}
          <p className="font-bold text-sm leading-tight truncate" style={{ color: '#1E293B' }} data-testid={`of-artigo-${of.numero_of}`}>
            {of.artigo || '\u2014'}
          </p>
          {/* Line 3: Marca/Cliente */}
          <p className="text-xs font-medium truncate mt-0.5" style={{ color: '#94A3B8' }} data-testid={`of-cliente-${of.numero_of}`}>
            {of.cliente || '\u2014'}
          </p>
        </div>

        {/* Right: Stats + Arrow */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Quantity */}
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#94A3B8' }}>Qt.</p>
            <p className="font-black text-base leading-none" style={{ color: '#1E293B' }}>
              {qtdTotal}<span className="text-xs font-semibold" style={{ color: '#94A3B8' }}>un</span>
            </p>
          </div>
          {/* Lotes */}
          <div className="text-center">
            <p className="font-black text-base leading-none" style={{ color: '#1E293B' }}>{lotes.length}</p>
            <p className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#94A3B8' }}>LOTES</p>
          </div>
          {/* Ops — mostrar feitas/total */}
          <div className="text-center">
            <p className="font-black text-base leading-none" style={{ color: todasConcluidas ? '#10B981' : '#1E293B' }} data-testid={`of-ops-progress-${of.numero_of}`}>
              {opsTotal > 0 ? `${opsConcluidasCount}/${opsTotal}` : uniqueOps}
            </p>
            <p className="text-[9px] font-bold uppercase tracking-wide" style={{ color: todasConcluidas ? '#10B981' : '#94A3B8' }}>
              {todasConcluidas ? 'CONCL.' : 'OPS'}
            </p>
          </div>
          {/* Arrow */}
          <ChevronRight className="h-5 w-5" style={{ color: '#CBD5E1' }} />
        </div>
      </div>
    </div>
  );
}
